import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { RefreshCw, CheckCircle, AlertCircle } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

export default function RedditCallback() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing')
  const [message, setMessage] = useState('Processing Reddit connection...')

  useEffect(() => {
    handleCallback()
  }, [])

  const handleCallback = async () => {
    try {
      const code = searchParams.get('code')
      const state = searchParams.get('state')
      const error = searchParams.get('error')

      // Check for OAuth errors
      if (error) {
        throw new Error(`Reddit OAuth error: ${error}`)
      }

      if (!code || !state) {
        throw new Error('Missing authorization code or state parameter')
      }

      // Verify state matches what we stored
      const storedState = localStorage.getItem('reddit_oauth_state')
      const projectId = localStorage.getItem('reddit_oauth_project_id')

      if (!storedState || storedState !== state) {
        throw new Error('Invalid state parameter - possible CSRF attack')
      }

      if (!projectId) {
        throw new Error('Missing project ID from OAuth flow')
      }

      // Get current user
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        throw new Error('User not authenticated')
      }

      setMessage('Exchanging authorization code for access token...')

      // Call our Edge Function to handle the OAuth callback
      console.log('Calling reddit-oauth-callback with:', {
        code: code ? 'PRESENT' : 'MISSING',
        state: state ? 'PRESENT' : 'MISSING', 
        projectId,
        userId: user.id
      })
      
      const { data, error: callbackError } = await supabase.functions.invoke('reddit-oauth-callback', {
        body: {
          code,
          state,
          projectId,
          userId: user.id
        }
      })

      console.log('Edge Function response:', { data, error: callbackError })

      if (callbackError) {
        console.error('Edge Function error details:', callbackError)
        throw callbackError
      }

      if (data.status === 'error') {
        throw new Error(data.message)
      }

      // Get the return URL and clean up localStorage
      const returnUrl = localStorage.getItem('reddit_oauth_return_url')
      localStorage.removeItem('reddit_oauth_state')
      localStorage.removeItem('reddit_oauth_project_id')
      localStorage.removeItem('reddit_oauth_return_url')

      setStatus('success')
      setMessage(`Successfully connected Reddit account: u/${data.data.reddit_username}`)

      // Invalidate queries to refresh Reddit connection status
      queryClient.invalidateQueries({ queryKey: ['reddit-connection'] })
      queryClient.invalidateQueries({ queryKey: ['reddit-accounts'] })

      // Redirect back to where they came from after a short delay
      setTimeout(() => {
        if (returnUrl) {
          // Extract the path from the full URL to ensure we stay within the app
          const url = new URL(returnUrl)
          navigate(url.pathname + url.search)
        } else {
          // Fallback to Reddit settings
          navigate(`/projects/${projectId}/reddit/settings`)
        }
      }, 2000)

    } catch (error) {
      console.error('Reddit OAuth callback error:', error)
      setStatus('error')
      setMessage(error.message || 'Failed to connect Reddit account')
      
      toast({
        title: "Connection Failed",
        description: error.message || 'Failed to connect Reddit account',
        variant: "destructive"
      })

      // Clean up localStorage on error
      localStorage.removeItem('reddit_oauth_state')
      localStorage.removeItem('reddit_oauth_project_id')
      localStorage.removeItem('reddit_oauth_return_url')
    }
  }

  const handleRetry = () => {
    const projectId = localStorage.getItem('reddit_oauth_project_id')
    if (projectId) {
      navigate(`/projects/${projectId}/reddit/settings`)
    } else {
      navigate('/projects')
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-50 to-zinc-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-xl font-semibold text-zinc-900">
            Reddit Connection
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-6">
          {status === 'processing' && (
            <>
              <div className="flex justify-center">
                <RefreshCw className="h-12 w-12 text-blue-600 animate-spin" />
              </div>
              <p className="text-zinc-600">{message}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="flex justify-center">
                <div className="bg-green-100 rounded-full p-3">
                  <CheckCircle className="h-12 w-12 text-green-600" />
                </div>
              </div>
              <div>
                <p className="text-green-700 font-medium">Connection Successful!</p>
                <p className="text-zinc-600 text-sm mt-1">{message}</p>
                <p className="text-zinc-500 text-sm mt-2">Redirecting you back...</p>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="flex justify-center">
                <div className="bg-red-100 rounded-full p-3">
                  <AlertCircle className="h-12 w-12 text-red-600" />
                </div>
              </div>
              <div>
                <p className="text-red-700 font-medium">Connection Failed</p>
                <p className="text-zinc-600 text-sm mt-1">{message}</p>
              </div>
              <Button onClick={handleRetry} className="w-full">
                Back to Settings
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
} 